import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { JobsService } from 'src/app/services/jobs.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-job-filter',
  templateUrl: './job-filter.component.html',
  styleUrls: ['./job-filter.component.scss']
})
export class JobFilterComponent implements OnInit {

  @Input() showFilterModal: boolean = false;

  modalRef: NgbModalRef = null;
  modalConfigs: NgbModalOptions = { size: "md", centered: true, backdrop: 'static', keyboard: false};
  @ViewChild('modalElmRef', { static: false }) modalElmRef!: ElementRef;

  @Output() statusEmitter: EventEmitter<{status: string}> = new EventEmitter();

  // for role
  selectedRole: string | null = null;
  searchRole$: any = new Subject<any>();
  searchRolePage: any = 1;
  roleKeyword: string = '';
  isloadingRoles: boolean = false;
  rolesList: any[] = [];

  // for skill
  selectedSkill: string | null = null;
  searchSkill$ = new Subject<any>();
  searchSkillPage:any = 1;
  skillKeyword: string = '';
  isLoadingSkills: boolean = false;
  skillsList: any[] = [];

  // for location
  selectedLocation: string | null = null;
  searchLocation$ = new Subject<any>();
  searchLocationPage: any = 1;
  locationkeyword: string = '';
  isLoadingLocations: boolean = false;
  locationList: any[] = [];

  // Filters
  experienceYears: string[] = Array.from({ length: 31 }, (_, i) =>
    i + (i === 1 ? ' year' : ' years')
  );

  experience: string = '';
  salary: string = '';

  min_salary: number = null;
  max_salary: number = null;

  // For scrolling
  noMoreRoles: boolean = false;
  noMoreSkills: boolean = false;
  noMoreLocations: boolean = false;

  constructor(private modalService: NgbModal, private jobService: JobsService, private ns: NotificationService) { }

  /**
   * To initialize data.
   */
  ngOnInit() {
    this.initFilterSubscriptions();
  }

  initFilterSubscriptions() {
    this.searchRole$.pipe(
      debounceTime(300),
    ).subscribe(() => {
      if (this.isloadingRoles || this.noMoreRoles) return;
      this.loadRoles();
    });

    this.searchSkill$.pipe(
      debounceTime(300),
    ).subscribe(() => {
      if (this.isLoadingSkills || this.noMoreSkills) return;
      this.loadSkills();
    });

    this.searchLocation$.pipe(
      debounceTime(300),
    ).subscribe(() => {
      if (this.isLoadingLocations || this.noMoreLocations) return;
      this.loadLocations();
    });
  }

  /**
   * To open modal if show modal true.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['showFilterModal'] && changes['showFilterModal'].currentValue === true) {
      this.openModel();
    }
  }

  /**
   * To open modal popup.
   */
  openModel() {
    this.modalRef = this.modalService.open(this.modalElmRef, this.modalConfigs);
  }

  /**
   * To close modal popup.
   */
  closeModel() {
    this.statusEmitter.emit({ status: 'closed' });
    this.modalRef.close();
  }

  /**
   * On cancel modal popup.
   */
  onCancel() {
    this.statusEmitter.emit({ status: 'cancelled' });
    this.modalRef.close();
  }

  /**
   * On confirm modal popup.
   */
  onConfirm() {
    let condition = this.updateFilterConditions('');
    if(this.experience) {
      condition['experience'] = this.experience;
    }

    if(this.salary) {
      condition['salary'] = this.salary;
      condition['minSalary'] = this.min_salary;
      condition['maxSalary'] = this.max_salary;
    }

    let applyFilter = {
      condition: condition,
      page: 1,
    }
    this.statusEmitter.emit({ status: 'confirmed' });
    this.modalRef.close();
  }

  onSearchRole(obj) {
    let searchTerm = obj["term"].trim() || '';
    this.roleKeyword = searchTerm;
    this.rolesList = [];
    this.searchRolePage = 1;
    this.searchRole$.next(searchTerm);
  }

  onClearRole() {
    this.rolesList = [];
    this.roleKeyword = '';
    this.searchRolePage = 1;
    this.loadRoles();
  }

  onScrollToEndRole() {
    this.searchRolePage++;
    this.loadRoles();
  }

  onFocusRole() {
    this.rolesList = [];
    this.searchRolePage = 1;
    this.loadRoles();
  }

  onSearchSkill(obj) {
    let searchTerm = obj["term"].trim() || '';
    this.skillKeyword = searchTerm;
    this.skillsList = [];
    this.searchSkillPage = 1;
    this.searchSkill$.next(searchTerm);
  }

  onClearSkill() {
    this.skillsList = [];
    this.skillKeyword = '';
    this.searchSkillPage = 1;
    this.loadSkills();
  }

  onScrollToEndSkill() {
    this.searchSkillPage++;
    this.loadSkills();
  }

  onFocusSkill() {
    this.skillsList = [];
    this.searchSkillPage = 1;
    this.loadSkills();
  }

  onSearchLocation(obj) {
    let searchTerm = obj["term"].trim() || '';
    this.locationkeyword = searchTerm;
    this.locationList = [];
    this.searchLocationPage = 1;
    this.searchLocation$.next(searchTerm);
  }

  onClearLocation() {
    this.locationList = [];
    this.locationkeyword = '';
    this.searchLocationPage = 1;
    this.loadLocations();
  }

  onScrollToEndlocation() {
    this.searchLocationPage++;
    this.loadLocations();
  }

  onFocusLocation() {
    this.locationList = []
    this.searchLocationPage = 1;
    this.loadLocations();
  }

  onSalaryRangeChange(value: string): void {
    if (value === '50+') {
      this.min_salary = 50 * 100000;
      this.max_salary = null;
    } else {
      const [min, max] = value.split('-').map(Number);
      this.min_salary = min * 100000;
      this.max_salary = max * 100000;
    }
  }

  updateFilterConditions(filterName: string) {
    let filterObj = {}
    if (this.selectedLocation) {
      filterObj["location"] = this.selectedLocation.trim();
    }
    if (this.selectedSkill) {
      filterObj["tags"] = this.selectedSkill.trim();
    }
    if (this.selectedRole) {
      filterObj["job_title"] = this.selectedRole.trim();
    }

    delete filterObj[filterName];

    return filterObj
  }

  loadRoles() {
    const applyFilter = {
      condition: { ...this.updateFilterConditions("job_title") },
      keyword: this.roleKeyword.trim(),
      column: "job_title",
      page: this.searchRolePage,
    };

    this.isloadingRoles = true;
    this.jobService.searchFilter(applyFilter).subscribe({
      next: (res) => {
        this.isloadingRoles = false;
        const newRoles = res["data"] || [];
        this.rolesList = [...this.rolesList, ...newRoles];
      },
      error: (err) => {
        this.isloadingRoles = false;
        this.ns.showNotification("error", err.message);
      }
    });
  }

  loadSkills() {
    let applyFilter = {
      condition: { ...this.updateFilterConditions("tags") },
      keyword: this.skillKeyword.trim(),
      column: "tags",
      page: this.searchSkillPage,
    };

    this.isLoadingSkills = true;
    this.jobService.searchFilter(applyFilter).subscribe({
      next: (res) => {
        this.isLoadingSkills = false;
        const newSkills = res["data"] || [];
        this.skillsList = [...this.skillsList, ...newSkills]
      },
      error: (err) => {
        this.isLoadingSkills = false;
        this.ns.showNotification("error", err.message);
      }
    });
  }

  loadLocations() {
    let applyFilter = {
      condition: { ...this.updateFilterConditions("location") },
      keyword: this.locationkeyword.trim(),
      column: "location",
      page: this.searchLocationPage,
    };

    this.isLoadingLocations = true;
    this.jobService.searchFilter(applyFilter).subscribe({
      next: (res) => {
        this.isLoadingLocations = false;
        const newLocations = res["data"] || [];
        this.locationList = [...this.locationList, ...newLocations];
      },
      error: (err) => {
        this.isLoadingLocations = false;
        this.ns.showNotification("error", err.message);
      }
    });
  }
}
